import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Globe, Building2, LogOut, Search, ArrowRight, AlertTriangle, LayoutDashboard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import privaxiaLogo from '@/assets/privaxia-logo.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DPOTenantSelectorPage = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/dpo/tenants`, { withCredentials: true });
        setTenants(data.tenants || []);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao carregar organizações');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectTenant = async (tenant) => {
    try {
      await axios.post(`${BACKEND_URL}/api/dpo/select-tenant`, { tenant_id: tenant.tenant_id }, { withCredentials: true });
      toast.success(`A operar em ${tenant.name}`);
      navigate('/dpo', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível selecionar a organização');
    }
  };

  const logout = async () => {
    await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate('/login-local');
  };

  const filtered = tenants.filter((t) =>
    `${t.name} ${t.slug || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0F172A] text-white" data-testid="dpo-tenant-selector">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-start justify-between mb-12">
          <div className="flex items-center gap-4">
            <img src={privaxiaLogo} alt="Privaxia" className="w-12 h-12 object-contain" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  DPO as a Service
                </h1>
                <Badge className="bg-[#10B981] text-white gap-1" data-testid="global-badge">
                  <Globe className="w-3 h-3" /> Global
                </Badge>
              </div>
              <p className="text-base text-slate-400 mt-2">{user?.email} · {tenants.length} organização(ões) sob gestão</p>
            </div>
          </div>
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={logout} data-testid="dpo-logout-button">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              data-testid="dpo-tenant-search"
              placeholder="Procurar organização..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
            />
          </div>
          <Button
            className="bg-[#10B981] hover:bg-[#0ea371]"
            onClick={() => navigate('/dpo/global')}
            data-testid="dpo-global-dashboard-button"
          >
            <LayoutDashboard className="w-4 h-4 mr-2" /> Painel consolidado
          </Button>
        </div>

        {loading ? (
          <p className="text-slate-400">A carregar organizações...</p>
        ) : filtered.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center text-slate-300">
              Sem organizações atribuídas. O Super Admin tem de ligar tenants ao DPO as a Service.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((tenant, index) => (
              <Card
                key={tenant.tenant_id}
                data-testid={`dpo-tenant-card-${tenant.slug || tenant.tenant_id}`}
                className="bg-white/5 border-white/10 hover:border-[#10B981]/60 transition-colors cursor-pointer group"
                style={{ animation: `fadeIn 400ms ease ${index * 60}ms both` }}
                onClick={() => selectTenant(tenant)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-[#10B981]" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">{tenant.name}</p>
                        <p className="text-xs text-slate-500">{tenant.slug}</p>
                      </div>
                    </div>
                    {tenant.requests_overdue > 0 ? (
                      <Badge variant="destructive" className="gap-1" data-testid={`overdue-badge-${tenant.slug}`}>
                        <AlertTriangle className="w-3 h-3" /> {tenant.requests_overdue} em atraso
                      </Badge>
                    ) : tenant.requests_at_risk > 0 ? (
                      <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 gap-1" data-testid={`at-risk-badge-${tenant.slug}`}>
                        <AlertTriangle className="w-3 h-3" /> {tenant.requests_at_risk} em risco
                      </Badge>
                    ) : tenant.requests_pending > 0 ? (
                      <Badge variant="outline" className="border-white/20 text-slate-300">
                        {tenant.requests_pending} pendente(s)
                      </Badge>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-center mb-5">
                    <div>
                      <p className="text-xl font-semibold text-white">{tenant.titulares}</p>
                      <p className="text-xs text-slate-500">Titulares</p>
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-white">{tenant.consents_active}</p>
                      <p className="text-xs text-slate-500">Consent. ativos</p>
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-white">{tenant.consents_expiring}</p>
                      <p className="text-xs text-slate-500">A expirar</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-slate-400 group-hover:text-[#10B981] transition-colors">
                    <span className="uppercase text-xs tracking-wide">{tenant.plan_key}</span>
                    <span className="flex items-center gap-1">Entrar <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px);} to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
};

export default DPOTenantSelectorPage;
